import { useState, useRef, useCallback } from 'react';

/**
 * Custom hook for managing game log notifications
 * Handles processing game log entries and creating notifications
 */
export const useGameNotifications = (playerName) => {
  const [logNotifications, setLogNotifications] = useState([]);
  const nextNotificationIdRef = useRef(0);
  const lastProcessedTimestampRef = useRef(null);
  const processedEntryIdsRef = useRef(new Set());
  const nameRef = useRef(playerName);

  // Update ref when playerName changes
  if (nameRef.current !== playerName) {
    nameRef.current = playerName;
  }

  const processGameLogUpdate = useCallback((newLog) => {
    if (!Array.isArray(newLog) || newLog.length === 0) return;
    
    // Create a unique ID for each entry (timestamp + message) to prevent duplicates
    const getEntryId = (entry) => {
      if (!entry || !entry.timestamp || !entry.message) return null;
      return `${entry.timestamp}-${entry.message.substring(0, 50)}`;
    };
    
    // Process all entries that haven't been processed yet
    const unprocessedEntries = newLog.filter(entry => {
      if (!entry || !entry.timestamp || !entry.message) return false;
      const entryId = getEntryId(entry);
      if (!entryId) return false;
      
      // Check if we've already processed this entry
      if (processedEntryIdsRef.current.has(entryId)) return false;
      
      // If we haven't processed anything yet, process all entries
      if (lastProcessedTimestampRef.current === null) return true;
      
      // Process entries with timestamp after or equal to the last processed one
      return entry.timestamp >= lastProcessedTimestampRef.current;
    });
    
    // Process each unprocessed entry
    unprocessedEntries.forEach(entry => {
      if (!entry || !entry.message) return;
      
      const entryId = getEntryId(entry);
      if (!entryId) return;
      
      // Mark as processed
      processedEntryIdsRef.current.add(entryId);
      
      // Extract player name from message to check if current player triggered it
      const message = entry.message;
      
      // Patterns for player-triggered actions (these should be hidden for the current player)
      const playerActionPatterns = [
        /💰\s+(\w+)\s+(?:purchased|sold)/,
        /📊\s+(\w+)\s+(?:purchased|sold).*index/,
        /🎴\s+(\w+)\s+played/,
        /🃏\s+(\w+)\s+drew/,
        /🤫\s+(\w+)\s+used/
      ];
      
      // Patterns for events that mention players but aren't actions (show for everyone)
      const playerMentionPatterns = [
        /💰\s+(\w+)\s+(?:received|paid)/,
        /✅\s+(\w+)\s+ended/,
      ];
      
      // Check if it's a round start message
      const isRoundStart = message.includes('Trade Round') && message.includes('has started');
      
      let playerName = null;
      let isPlayerAction = false;
      
      // Check if it's a player action
      for (const pattern of playerActionPatterns) {
        const match = message.match(pattern);
        if (match && match[1]) {
          playerName = match[1];
          isPlayerAction = true;
          break;
        }
      }
      
      // If not an action, check if it mentions a player (for avatar display)
      if (!playerName) {
        for (const pattern of playerMentionPatterns) {
          const match = message.match(pattern);
          if (match && match[1]) {
            playerName = match[1];
            isPlayerAction = false;
            break;
          }
        }
      }
      
      // Don't show notification if current player triggered the action
      // But always show round start messages
      if (isRoundStart || !(isPlayerAction && playerName && playerName === nameRef.current)) {
        const notificationId = nextNotificationIdRef.current;
        nextNotificationIdRef.current += 1;
        
        setLogNotifications(prev => [...prev, {
          id: notificationId,
          message: entry.message,
          playerName: playerName || null
        }]);
      }
    });
    
    // Update last processed timestamp
    if (newLog.length > 0) {
      const latestEntry = newLog[newLog.length - 1];
      if (latestEntry && latestEntry.timestamp) {
        if (lastProcessedTimestampRef.current === null || latestEntry.timestamp >= lastProcessedTimestampRef.current) {
          lastProcessedTimestampRef.current = latestEntry.timestamp;
        }
      }
    }
  }, []);

  const addNotification = useCallback((message, playerName = null) => {
    const notificationId = nextNotificationIdRef.current;
    nextNotificationIdRef.current += 1;
    setLogNotifications(prev => [...prev, {
      id: notificationId,
      message,
      playerName
    }]);
  }, []);

  const removeNotification = useCallback((id) => {
    setLogNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setLogNotifications([]);
  }, []);

  const initializeFromGameLog = useCallback((gameLog) => {
    if (Array.isArray(gameLog) && gameLog.length > 0) {
      const lastEntry = gameLog[gameLog.length - 1];
      if (lastEntry && lastEntry.timestamp) {
        lastProcessedTimestampRef.current = lastEntry.timestamp;
      }
    }
  }, []);

  return {
    logNotifications,
    processGameLogUpdate,
    addNotification,
    removeNotification,
    clearAllNotifications,
    initializeFromGameLog
  };
};

